import React, { useState, useMemo } from 'react';
import {
  Star,
  CheckCircle2,
  ExternalLink,
  Award,
  MessageSquareReply,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  TRUSTINDEX_REVIEWS,
  TRUSTINDEX_SUMMARY,
  TrustindexReview,
} from '../data/trustindexReviews';

type FilterMode = 'all' | 'written' | 'replied';

function ReviewerAvatar({ name, avatar }: { name: string; avatar: string }) {
  const [imgError, setImgError] = useState(false);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  if (avatar && !imgError) {
    return (
      <img
        src={avatar}
        alt={name}
        width={44}
        height={44}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
        className="w-11 h-11 rounded-full object-cover border border-slate-200 shrink-0 bg-slate-100"
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className="w-11 h-11 rounded-full bg-[#0B2D52] text-white font-extrabold text-xs flex items-center justify-center shrink-0 border border-[#0B2D52]/20"
    >
      {initials || 'XW'}
    </div>
  );
}

function GoogleBadgeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-4 h-4 shrink-0"
      aria-label="Verified Google Review"
    >
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"
      />
    </svg>
  );
}

export const TrustindexReviewsSection: React.FC = () => {
  const [filterMode, setFilterMode] = useState<FilterMode>('written');
  const [showAll, setShowAll] = useState(false);

  const filteredReviews = useMemo(() => {
    if (filterMode === 'written') {
      return TRUSTINDEX_REVIEWS.filter((r) => r.text.trim().length > 0);
    }
    if (filterMode === 'replied') {
      return TRUSTINDEX_REVIEWS.filter((r) => Boolean(r.replyText));
    }
    return TRUSTINDEX_REVIEWS;
  }, [filterMode]);

  const visibleReviews = showAll ? filteredReviews : filteredReviews.slice(0, 6);

  const writtenCount = useMemo(
    () => TRUSTINDEX_REVIEWS.filter((r) => r.text.trim().length > 0).length,
    []
  );
  const repliedCount = useMemo(
    () => TRUSTINDEX_REVIEWS.filter((r) => Boolean(r.replyText)).length,
    []
  );

  return (
    <section
      id="verified-reviews"
      aria-labelledby="trustindex-reviews-heading"
      className="py-20 bg-white border-t border-slate-200"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl space-y-2.5">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-700 font-extrabold">
                TRUSTINDEX & GOOGLE VERIFIED
              </span>
              <span aria-hidden="true" className="text-slate-300">
                ·
              </span>
              <span>TOP RATED CERTIFICATE</span>
            </div>
            <h2
              id="trustindex-reviews-heading"
              className="text-3xl sm:text-4xl font-extrabold text-[#0B2D52] tracking-tight"
            >
              Rated {TRUSTINDEX_SUMMARY.ratingScore} out of 5 by real clients
            </h2>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Unedited client reviews verified by Trustindex and collected from Google Business Profile for{' '}
              <span className="font-semibold text-[#0B2D52]">xweba.com</span>.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={TRUSTINDEX_SUMMARY.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-[#0B2D52] text-white text-xs font-extrabold transition-colors shadow-2xs"
            >
              <span>Verify on Trustindex.io</span>
              <ExternalLink className="w-3.5 h-3.5 text-[#F26522]" />
            </a>
          </div>
        </div>

        {/* Summary Dashboard Banner */}
        <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-6 sm:p-8 mb-10 shadow-2xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left: Overall Score & Certificate */}
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start sm:items-center gap-5 lg:border-r lg:border-slate-200 lg:pr-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center min-w-[150px] shadow-2xs">
                <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 mb-1">
                  {TRUSTINDEX_SUMMARY.ratingLabel}
                </div>
                <div className="text-4xl font-black text-[#0B2D52] tracking-tight">
                  {TRUSTINDEX_SUMMARY.ratingScore}
                  <span className="text-lg font-bold text-slate-400">/5</span>
                </div>
                <div
                  className="flex items-center justify-center gap-0.5 my-2"
                  aria-label="4.9 out of 5 stars"
                >
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <div className="text-[11px] font-semibold text-slate-500">
                  Based on{' '}
                  <strong className="text-slate-800">
                    {TRUSTINDEX_SUMMARY.totalReviews} reviews
                  </strong>
                </div>
              </div>

              <div className="space-y-2.5">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold">
                  <Award className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Top Rated Certificate</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Trustindex verifies that <strong className="text-slate-900">XwebA</strong> maintains a verified review score above 4.5 with{' '}
                  <strong className="text-slate-900">
                    {TRUSTINDEX_SUMMARY.fiveStarPercentage}% five-star ratings
                  </strong>
                  .
                </p>
                <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                  <GoogleBadgeIcon />
                  <span>Source: Google & Trustindex · Shown unedited</span>
                </div>
              </div>
            </div>

            {/* Middle: Star Breakdown Bars */}
            <div className="lg:col-span-5 space-y-2 lg:border-r lg:border-slate-200 lg:pr-6">
              <div className="text-xs font-extrabold uppercase tracking-wider text-slate-400 mb-2">
                Rating Breakdown
              </div>
              {TRUSTINDEX_SUMMARY.breakdown.map((row) => (
                <div key={row.stars} className="flex items-center gap-3 text-xs">
                  <span className="w-14 font-bold text-slate-700 shrink-0">
                    {row.stars} {row.stars === 1 ? 'star' : 'stars'}
                  </span>
                  <div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${row.percentage}%` }}
                    />
                  </div>
                  <span className="w-16 text-right font-mono text-[11px] font-semibold text-slate-500 shrink-0">
                    {row.count} {row.count === 1 ? 'pc' : 'pcs'} ({row.percentage}%)
                  </span>
                </div>
              ))}
            </div>

            {/* Right: Filter Controls & Trust Metrics */}
            <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
              <div className="space-y-1.5">
                <div className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
                  Filter Verified Feed
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterMode('written');
                      setShowAll(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-between ${
                      filterMode === 'written'
                        ? 'bg-[#0B2D52] text-white shadow-2xs'
                        : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span>Written Reviews</span>
                    <span
                      className={`text-[11px] font-mono ${
                        filterMode === 'written' ? 'text-[#F26522]' : 'text-slate-400'
                      }`}
                    >
                      {writtenCount}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setFilterMode('replied');
                      setShowAll(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-between ${
                      filterMode === 'replied'
                        ? 'bg-[#0B2D52] text-white shadow-2xs'
                        : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span>With XwebA Reply</span>
                    <span
                      className={`text-[11px] font-mono ${
                        filterMode === 'replied' ? 'text-[#F26522]' : 'text-slate-400'
                      }`}
                    >
                      {repliedCount}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setFilterMode('all');
                      setShowAll(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-between ${
                      filterMode === 'all'
                        ? 'bg-[#0B2D52] text-white shadow-2xs'
                        : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span>All Star Ratings</span>
                    <span
                      className={`text-[11px] font-mono ${
                        filterMode === 'all' ? 'text-[#F26522]' : 'text-slate-400'
                      }`}
                    >
                      {TRUSTINDEX_REVIEWS.length}
                    </span>
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>{TRUSTINDEX_SUMMARY.replyTimeLabel}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleReviews.map((review: TrustindexReview) => (
            <article
              key={review.id}
              className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
            >
              <div>
                {/* Reviewer Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <ReviewerAvatar name={review.name} avatar={review.avatar} />
                    <div className="min-w-0">
                      <h3 className="text-sm font-extrabold text-[#0B2D52] truncate">
                        {review.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
                        <time dateTime={review.date}>{review.date}</time>
                        <span aria-hidden="true">·</span>
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Verified
                        </span>
                      </div>
                    </div>
                  </div>
                  <GoogleBadgeIcon />
                </div>

                {/* Star Rating */}
                <div
                  className="flex items-center gap-1 mb-3"
                  aria-label={`${review.rating} out of 5 stars`}
                >
                  {[...Array(5)].map((_, idx) => (
                    <Star
                      key={idx}
                      className={`w-4 h-4 ${
                        idx < review.rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'fill-slate-200 text-slate-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Review Text */}
                {review.text ? (
                  <p className="text-sm text-slate-700 leading-relaxed">
                    “{review.text}”
                  </p>
                ) : (
                  <p className="text-xs italic text-slate-400">
                    Verified {review.rating}-star rating submitted on Google without written comment.
                  </p>
                )}
              </div>

              {/* Owner Reply from XwebA */}
              {review.replyText && (
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-[#0B2D52]">
                        <MessageSquareReply className="w-3.5 h-3.5 text-[#F26522]" />
                        Reply from XwebA
                      </span>
                      {review.replyDate && (
                        <span className="text-[10px] font-mono text-slate-400">
                          {review.replyDate}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {review.replyText}
                    </p>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Expand / Collapse All Button */}
        {filteredReviews.length > 6 && (
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-[#0B2D52] text-xs font-extrabold transition-colors cursor-pointer"
            >
              {showAll ? (
                <>
                  <span>Show Fewer Reviews</span>
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>
                    Show All {filteredReviews.length} Verified Reviews
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>

            <a
              href={TRUSTINDEX_SUMMARY.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#F26522] transition-colors"
            >
              <span>Read full certificate on Trustindex.io</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
